import {
	fetchCollegeOptions,
	fetchTermOptions,
	fetchCourses,
	fetchSearch,
	fetchSearchPage,
} from '@/api/fetch.js';
import {
	buildCollegeOptionsParams,
	buildTermOptionsParams,
	buildCourseParams,
	buildSearchParams,
} from '@/engine/query-params.js';

/**
 * @typedef {import('@/engine/filter-graph.js').StepId} StepId
 * @typedef {import('@/types').EngineSnapshot} EngineSnapshot
 * @typedef {import('@/types').PhaseMeta} PhaseMeta
 * @typedef {import('@/types').StepRuntime} StepRuntime
 * @typedef {import('@/types').SearchConfig} SearchConfig
 */

/**
 * @typedef {Object} StepResult
 * @property {StepId} stepId
 * @property {unknown} [data]
 * @property {PhaseMeta} [phaseMeta]
 * @property {PhaseMeta} [planMeta]
 * @property {StepRuntime} [runtime]
 */

/**
 * Pure fetch + local side-effect-free steps.
 * Mutations happen in apply-step.js after this returns.
 *
 * @param {StepId} stepId
 * @param {EngineSnapshot} snapshot
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @param {PhaseMeta} [options.phaseMeta]
 * @param {PhaseMeta} [options.planMeta]
 * @param {StepRuntime} [options.runtime]
 * @returns {Promise<StepResult>}
 */
export async function runStep(stepId, snapshot, options = {}) {
	let { signal, phaseMeta = {}, planMeta = {}, runtime = {} } = options;

	if (stepId === 'resetCourse') {
		return { stepId, phaseMeta, planMeta, runtime };
	}

	switch (stepId) {
		case 'collegeOptions':
			return {
				stepId,
				data: await fetchCollegeOptions(buildCollegeOptionsParams(snapshot), signal),
				phaseMeta,
				planMeta,
				runtime,
			};
		case 'termOptions':
			return {
				stepId,
				data: await fetchTermOptions(buildTermOptionsParams(snapshot), signal),
				phaseMeta,
				planMeta,
				runtime,
			};
		case 'courses':
			return {
				stepId,
				data: await fetchCourses(buildCourseParams(snapshot), signal),
				phaseMeta,
				planMeta,
				runtime,
			};
		case 'search': {
			/** @type {SearchConfig} */
			let searchConfig = phaseMeta.search ?? planMeta.search ?? { update: true };
			let searchParams = buildSearchParams(snapshot, {
				page: runtime.page ?? 1,
			});
			return {
				stepId,
				data: await fetchSearch(searchParams, signal),
				phaseMeta: { ...phaseMeta, search: searchConfig },
				planMeta,
				runtime,
			};
		}
		case 'searchPage': {
			let searchParams = buildSearchParams(snapshot, {
				page: runtime.page,
			});
			return {
				stepId,
				data: await fetchSearchPage(searchParams, signal),
				phaseMeta,
				planMeta,
				runtime,
			};
		}
		default:
			throw new Error(`Unknown step id: ${stepId}`);
	}
}
